import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang, tField } from "../lib/LanguageContext";
import { useCurrency } from "../lib/CurrencyContext";
import { api, imageUrl, whatsappLink } from "../lib/api";
import SEO, { roomSchema, breadcrumbSchema } from "../components/SEO";

function RoomImageCarousel({ images, alt }) {
    const [idx, setIdx] = useState(0);
    const list = images?.length ? images : ["seed:room_standard"];

    return (
        <div className="relative aspect-[4/3] md:aspect-auto md:h-full bg-[#F4EFE6] overflow-hidden">
            <img decoding="async" loading="lazy"
                src={imageUrl(list[idx])}
                alt={alt}
                className="w-full h-full object-cover transition-opacity duration-500"
            />
            {list.length > 1 && (
                <>
                    <button
                        onClick={() => setIdx((idx - 1 + list.length) % list.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur hover:bg-white text-[#0F3A59] flex items-center justify-center"
                        aria-label="prev"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        onClick={() => setIdx((idx + 1) % list.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 backdrop-blur hover:bg-white text-[#0F3A59] flex items-center justify-center"
                        aria-label="next"
                    >
                        <ChevronRight size={18} />
                    </button>
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {list.map((img, i) => (
                            <button
                                key={img}
                                onClick={() => setIdx(i)}
                                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-white" : "w-1.5 bg-white/60"}`}
                                aria-label={`image ${i + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

export default function Rooms() {
    const { t, lang } = useLang();
    const { format } = useCurrency();
    const { site } = useOutletContext() || {};
    const [rooms, setRooms] = useState([]);

    useEffect(() => {
        api.get("/rooms").then((r) => setRooms(r.data)).catch(() => {});
    }, []);

    const whatsapp = site?.whatsapp || "355682116731";

    return (
        <div data-testid="rooms-page">
            <SEO
                title={lang === "al" ? "Dhomat" : "Rooms"}
                description={lang === "al" ? "Dhoma Standard, Gabina dhe Apartamente në Kompleksin Dea Radhimë — me pamje deti, Wi-Fi falas, çmime transparente." : "Standard rooms, Gabina bungalows and Apartments at Kompleksi Dea Radhimë — seaview, free Wi-Fi, transparent pricing."}
                path="/rooms"
                schemas={[
                    ...(rooms || []).map((r) => roomSchema(r)).filter(Boolean),
                    breadcrumbSchema([
                        { name: lang === "al" ? "Kreu" : "Home", url: "/" },
                        { name: t("nav_rooms"), url: "/rooms" },
                    ]),
                ]}
            />
            {/* Page Header */}
            <section className="relative h-[55vh] min-h-[400px] flex items-end overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: `url(${imageUrl(site?.rooms_hero_image, imageUrl("seed:room_gabina"))})`,
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/40" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16 pt-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">
                            {t("nav_rooms")}
                        </span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {t("featured_rooms")}
                        </h1>
                        <p className="text-white/85 font-light text-lg mt-4 max-w-2xl">
                            {t("rooms_intro")}
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Rooms list */}
            <section className="py-20 md:py-28 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex flex-col gap-16 md:gap-24">
                    {rooms.map((r, i) => (
                        <motion.div
                            key={r.id}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.7 }}
                            data-testid={`room-card-${r.slug}`}
                            className={`grid md:grid-cols-2 gap-8 md:gap-14 items-stretch ${i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""}`}
                        >
                            <div className="rounded-2xl overflow-hidden shadow-[0_20px_60px_rgb(0,0,0,0.08)] min-h-[320px] md:min-h-[480px]">
                                <RoomImageCarousel
                                    images={r.images}
                                    alt={tField(r, "title", lang)}
                                />
                            </div>
                            <div className="flex flex-col justify-center">
                                <span className="text-xs tracking-[0.3em] uppercase text-[#0F3A59]/60">
                                    0{i + 1}
                                </span>
                                <h2 className="font-serif text-4xl md:text-5xl text-[#0F3A59] tracking-tight mt-3">
                                    {tField(r, "title", lang)}
                                </h2>
                                <p className="text-[#1C1C1C]/75 font-light mt-5 leading-relaxed">
                                    {tField(r, "description", lang)}
                                </p>
                                {(lang === "al" ? r.features_al : r.features_en)?.length > 0 && (
                                    <ul className="grid grid-cols-2 gap-2.5 mt-6">
                                        {(lang === "al" ? r.features_al : r.features_en).map(
                                            (f) => (
                                                <li
                                                    key={f}
                                                    className="flex items-center gap-2 text-sm text-[#1C1C1C]/75 font-light"
                                                >
                                                    <Check
                                                        size={14}
                                                        className="text-[#CBA871]"
                                                        strokeWidth={2}
                                                    />
                                                    {f}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                )}
                                <div className="flex items-end justify-between mt-10 pt-8 border-t border-[#E5E0D8] flex-wrap gap-4">
                                    <div>
                                        <div className="text-xs text-[#1C1C1C]/60 uppercase tracking-wider">
                                            {t("from_price")}
                                        </div>
                                        <div className="font-serif text-3xl text-[#0F3A59]">
                                            {format(r.price)}
                                            <span className="text-base font-sans text-[#1C1C1C]/60 ml-1">
                                                {t("per_night")}
                                            </span>
                                        </div>
                                    </div>
                                    <Link
                                        to={`/book?room=${r.slug}`}
                                        data-testid={`room-book-${r.slug}`}
                                        className="inline-flex items-center gap-2 bg-[#0F3A59] hover:bg-[#1C5279] text-white px-7 py-3.5 rounded-full text-sm tracking-wide transition-colors"
                                    >
                                        {t("book_now")}
                                        <ArrowRight size={16} />
                                    </Link>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>
        </div>
    );
}
